import React, { useState } from "react";
import {
  Card,
  Switch,
  Separator,
  Spinner,
  Button,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@lemonadetraining/web-ui";
import { updateProgrammingControlSetting } from "@lemonadetraining/web-shared";
import { notifyActions, NOTIFICATION_TYPE } from "@lemonadetraining/web-shared";
import { useDispatch } from "react-redux";
import MyModal from "@lemonadetraining/web-shared/components/MyModal";
import * as PROGRAMMING from "@lemonadetraining/web-shared/constants/ProgrammingConst";

import { IconTrash, IconMailForward } from "@tabler/icons-react";

/* Own Modules */
import ProductArchiveWidget_Modal from "../../Widgets/ProductArchiveWidget_Modal";
import SendingInvitationWidget from "../../Widgets/SendingInvitationWidget.tsx";

/* =================== Program Control Panel ==================== */

interface ProgramControlPanelProps {
  programData: any;
  programUID: string;
  isLoading?: boolean;
}

function ProgramControlPanel(props: ProgramControlPanelProps) {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [lock, setLock] = useState(false);
  const isInactive = !props.programData.active;
  const [inviteModal, setInviteModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [controlSetting, setControlSetting] = useState<Record<string, boolean>>({
    isPremium: props.programData.isPremium || false,
    landingPage: props.programData.landingPage || false,
    isHistoryAvailable: props.programData.isHistoryAvailable || false,
    releaseAll: props.programData.releaseAll || false,
  });

  const isFix = props.programData?.programmingType.type === PROGRAMMING.TYPE.fix || false;

  const isTimeline = props.programData?.programmingType.type === PROGRAMMING.TYPE.timeline || false;

  /* check if it is PT programming */
  const isPT = props.programData?.isPT || false;

  /* Handling invite athlete */
  const onInviteAthlete = () => {
    setInviteModal(true);
  };

  const handleInviteModalClose = () => {
    setInviteModal(false);
  };

  /* Handling setting changes */
  const settingChange = (key: string, value: boolean) => {
    const temp = { ...controlSetting };
    temp[key] = value;

    setLock(true);
    setIsLoading(true);

    // update the backend,
    updateProgrammingControlSetting({
      programmingUID: props.programUID,
      settings: temp,
    })
      .then(() => {
        setControlSetting(temp);
        const notification = {
          severity: NOTIFICATION_TYPE.success,
          title: "Setting Updated!",
        };
        dispatch(notifyActions.sendNotification(notification));
      })
      .catch((error: any) => {
        const notification = {
          severity: NOTIFICATION_TYPE.error,
          title: "Update Error!",
          message: error.message,
        };
        dispatch(notifyActions.sendNotification(notification));
      })
      .finally(() => {
        setLock(false);
        setIsLoading(false);
      });
  };

  return (
    <div className="mt-5">
      <ProductArchiveWidget_Modal
        isOpen={showArchiveModal}
        onClose={() => setShowArchiveModal(false)}
        productId={props.programUID}
        productType={props.programData.productType}
      />
      {inviteModal && (
        <MyModal
          open={inviteModal}
          onClose={handleInviteModalClose}
          title={`Invite to ${props.programData.name}`}
          showTitle={false}
        >
          <SendingInvitationWidget
            title={props.programData.name}
            ofType="toProgramming"
            productType={props.programData.productType}
            productID={props.programUID}
            productOwnerID={props.programData.ownerUID}
            onClose={handleInviteModalClose}
          />
        </MyModal>
      )}
      <Card className="border-none shadow-none bg-transparent p-4">
        <div>
          <div className="flex flex-row justify-between">
            <div className="flex flex-row gap-2">
              <div className="flex flex-col">
                <h4 className="text-base font-medium">Control Panel</h4>
                {isPT && (
                  <p className="text-sm text-muted-foreground">
                    Personal Trainer Programming. it's Invitation only.
                  </p>
                )}
              </div>
              {(isLoading || props.isLoading) && <Spinner className="size-4" />}
            </div>
            <Button variant="destructive" onClick={() => setShowArchiveModal(true)} disabled={lock}>
              Archive <IconTrash className="ml-1 size-4" />
            </Button>
          </div>
        </div>
        <Separator className="my-3" />
        <TooltipProvider>
          <div className="flex flex-row gap-2 items-center">
            {!isPT && (
              <>
                {false && (
                  <>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <label className="flex items-center gap-2">
                          Is Premium
                          <Switch
                            disabled={isPT || lock || isInactive}
                            checked={controlSetting.isPremium}
                            onCheckedChange={(checked) => settingChange("isPremium", checked)}
                          />
                        </label>
                      </TooltipTrigger>
                      <TooltipContent>
                        Is this Programming part of Premium Package. [Coming Soon Feature]
                      </TooltipContent>
                    </Tooltip>
                    <Separator orientation="vertical" className="h-6" />
                  </>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <label className="flex items-center gap-2 data-[disabled]:opacity-50">
                      Landing Page
                      <Switch
                        disabled={isPT || lock || isInactive}
                        checked={controlSetting.landingPage}
                        onCheckedChange={(checked) => settingChange("landingPage", checked)}
                      />
                    </label>
                  </TooltipTrigger>
                  <TooltipContent>
                    Should this program be visible on your landing page or not.
                  </TooltipContent>
                </Tooltip>
                <Separator orientation="vertical" className="h-6" />
              </>
            )}
            {!isFix && !isTimeline && (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <label className="flex items-center gap-2 data-[disabled]:opacity-50">
                      Is History Available To New Athletes
                      <Switch
                        disabled={isPT || lock || isInactive}
                        checked={controlSetting.isHistoryAvailable}
                        onCheckedChange={(checked) => settingChange("isHistoryAvailable", checked)}
                      />
                    </label>
                  </TooltipTrigger>
                  <TooltipContent>
                    On: New athlete will get all the history of the programming. Off: New athlete
                    will view programming from the time they joined.
                  </TooltipContent>
                </Tooltip>
                <Separator orientation="vertical" className="h-6" />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <label className="flex items-center gap-2 data-[disabled]:opacity-50">
                      Release All Workouts
                      <Switch
                        disabled={lock || isInactive}
                        checked={controlSetting.releaseAll}
                        onCheckedChange={(checked) => settingChange("releaseAll", checked)}
                      />
                    </label>
                  </TooltipTrigger>
                  <TooltipContent>
                    On: Release all available workouts to athletes. Off: Release workouts on Sunday
                    before week starts (athlete local time).
                  </TooltipContent>
                </Tooltip>
                <Separator orientation="vertical" className="h-6" />
              </>
            )}
            <div className="flex-1" />
            <Button disabled={lock || isInactive} onClick={onInviteAthlete}>
              Invite Athletes <IconMailForward className="ml-1 size-4" />
            </Button>
          </div>
        </TooltipProvider>
      </Card>
    </div>
  );
}

export default ProgramControlPanel;
